import type { CRMUser, EmployeeDesignation, ReleaseAnnouncement, User, UserRole } from '../types';
import {
  isArchitectRole,
  isCrmTestUser,
  isCrmWorkstationUser,
  isDesignerRole,
  isDesignPartnerOrEngineerRole,
} from './userRoles';

const RELEASE_INDIVIDUAL_NOTIFY_ROLES = new Set<UserRole>([
  'employee',
  'employee_shop',
  'employee_office',
  'admin',
  'staff',
]);

export function parseAudienceUserIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    const id = typeof item === 'string' ? item.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export interface ReleaseNotifyPeopleOptions {
  /** Also list QA/test accounts. */
  includeTestUsers?: boolean;
  /** Also list designers, architects and engineers (partner roles that use Project Hub). */
  includeDesignPartners?: boolean;
}

/** Internal teammates who can be picked by name on the publish modal. */
export function isReleaseIndividualNotifyUser(
  user: Pick<CRMUser, 'isActive' | 'role' | 'isTestUser' | 'isWorkstation'>,
  opts: ReleaseNotifyPeopleOptions = {},
): boolean {
  if (!user.isActive) return false;
  if (isCrmWorkstationUser(user)) return false;
  if (!opts.includeTestUsers && isCrmTestUser(user)) return false;
  if (RELEASE_INDIVIDUAL_NOTIFY_ROLES.has(user.role)) return true;
  return !!opts.includeDesignPartners && isDesignPartnerOrEngineerRole(user.role);
}

export function listReleaseNotifyEmployees(
  crmUsers: CRMUser[],
  opts: ReleaseNotifyPeopleOptions = {},
): CRMUser[] {
  return crmUsers
    .filter(u => isReleaseIndividualNotifyUser(u, opts))
    .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '', undefined, { sensitivity: 'base' }));
}

/** Whether a signed-in user matches a release's role, designation, or named-person audience. */
export function releaseMatchesUserAudience(
  release: Pick<ReleaseAnnouncement, 'audienceRoles' | 'audienceDesignations' | 'audienceUserIds'>,
  user: User,
): boolean {
  if (release.audienceUserIds?.includes(user.id)) return true;
  if (release.audienceRoles.includes(user.role)) return true;
  if (
    isDesignerRole(user.role)
    && (release.audienceRoles.includes('architect') || release.audienceRoles.includes('designer'))
  ) {
    return true;
  }
  if (isArchitectRole(user.role) && release.audienceRoles.includes('architect')) {
    return true;
  }
  const designations = release.audienceDesignations;
  if (!designations?.length) return false;
  return !!user.designations?.some((d) => designations.includes(d));
}

export function hasReleaseAudienceSelection(
  audienceRoles: UserRole[],
  audienceDesignations: EmployeeDesignation[] = [],
  audienceUserIds: string[] = [],
): boolean {
  return audienceRoles.length > 0 || audienceDesignations.length > 0 || audienceUserIds.length > 0;
}
